import { useEffect, useRef } from "react";
import { defineStepper } from "@/components/stepper";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useFormContext } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import Signature, { type SignatureRef } from "@uiw/react-signature";

import { db } from "@/stores/indexed-db";
import { useMediaQuery } from "@uidotdev/usehooks";

import {
    personalInformationSchema,
    meansOfIdentificationSchema,
    residentialInformationSchema,
    clusterInformationSchema,
    type PersonalInformation,
    type MeansOfIdentification,
    type ResidentialInformation,
    type ClusterInformation
} from "@/doctypes/outgrower_farmer.zod";
import * as ENUMS from "@/models/enums";
import { OUTGROWER_COOPERATIVE_ROLES } from "@/models/outgrower-farmer";

const PersonalInfoForm = () => {
    const { register, formState: { errors }, setValue } = useFormContext<PersonalInformation>();

    return (
        <div className="space-y-4 text-start">
            <div className="space-y-2">
                <Label htmlFor={register("first_name").name}>First Name*</Label>
                <Input id={register("first_name").name} {...register("first_name")} className="block w-full rounded-md border p-2" />
                {errors.first_name && <p className="text-sm text-destructive">{errors.first_name.message}</p>}
            </div>
            <div className="space-y-2">
                <Label htmlFor={register("middle_name").name}>Middle Name</Label>
                <Input id={register("middle_name").name} {...register("middle_name")} className="block w-full rounded-md border p-2" />
                {errors.middle_name && <p className="text-sm text-destructive">{errors.middle_name.message}</p>}
            </div>
            <div className="space-y-2">
                <Label htmlFor={register("last_name").name}>Last Name*</Label>
                <Input id={register("last_name").name} {...register("last_name")} className="block w-full rounded-md border p-2" />
                {errors.last_name && <p className="text-sm text-destructive">{errors.last_name.message}</p>}
            </div>
            <div className="space-y-2">
                <Label htmlFor={register("phone_number").name}>Phone Number*</Label>
                <Input id={register("phone_number").name} {...register("phone_number")} className="block w-full rounded-md border p-2" />
                {errors.phone_number && <p className="text-sm text-destructive">{errors.phone_number.message}</p>}
            </div>
            <div className="space-y-2">
                <Label htmlFor={register("gender").name}>Gender</Label>
                <Select onValueChange={(value: typeof ENUMS.GENDERS[number]) => setValue("gender", value)}>
                    <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select Gender" />
                    </SelectTrigger>
                    <SelectContent>
                        {ENUMS.GENDERS.map((gender) => (
                            <SelectItem key={gender} value={gender}>
                                {gender}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {errors.gender && <p className="text-sm text-destructive">{errors.gender.message}</p>}
            </div>
            <div className="space-y-2">
                <Label htmlFor={register("date_of_birth").name}>Date of Birth</Label>
                <Input
                    id={register("date_of_birth").name}
                    {...register("date_of_birth")}
                    type="date"
                    className="block w-full rounded-md border p-2"
                />
                {errors.date_of_birth && <p className="text-sm text-destructive">{errors.date_of_birth.message}</p>}
            </div>
            <div className="space-y-2">
                <Label htmlFor={register("passport_photo").name}>Passport Photo</Label>
                <Input
                    id={register("passport_photo").name}
                    {...register("passport_photo")}
                    type="file"
                    className="block w-full rounded-md border px-2"
                />
                {errors.passport_photo && <p className="text-sm text-destructive">{errors.passport_photo.message}</p>}
            </div>
        </div>
    );
}

// Identification form component
const IdentificationForm = () => {
    const { register, formState: { errors }, setValue } = useFormContext<MeansOfIdentification>();
    const signatureRef = useRef<SignatureRef>(null);

    const handleSaveSignature = () => {
        if (signatureRef.current?.svg) {
            const svgData = signatureRef.current.svg;
            // Convert SVG to a string
            const svgString = new XMLSerializer().serializeToString(svgData);

            // Create a temporary SVG image to draw on canvas
            const img = new Image();
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            // Set dimensions for the image
            canvas.width = 500;
            canvas.height = 200;

            // Create a data URL representing the SVG
            const svgBlob = new Blob([svgString], { type: 'image/svg+xml' });
            const url = URL.createObjectURL(svgBlob);

            // Use a promise to handle the asynchronous loading of the image
            img.onload = () => {
                // Draw the image on the canvas
                if (ctx) {
                    ctx.fillStyle = 'white';
                    ctx.fillRect(0, 0, canvas.width, canvas.height);
                    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

                    // Convert canvas to PNG
                    const pngDataUrl = canvas.toDataURL('image/png');

                    // Convert data URL to blob
                    const byteString = atob(pngDataUrl.split(',')[1]);
                    const mimeString = pngDataUrl.split(',')[0].split(':')[1].split(';')[0];
                    const ab = new ArrayBuffer(byteString.length);
                    const ia = new Uint8Array(ab);

                    for (let i = 0; i < byteString.length; i++) {
                        ia[i] = byteString.charCodeAt(i);
                    }

                    const pngBlob = new Blob([ab], { type: mimeString });

                    // Create a File object
                    const file = new File([pngBlob], 'signature.png', { type: 'image/png' });

                    // Create a DataTransfer to get a FileList
                    const dataTransfer = new DataTransfer();
                    dataTransfer.items.add(file);

                    // Set the FileList to the signature field
                    setValue('signature', dataTransfer.files);

                    // Clean up
                    URL.revokeObjectURL(url);

                    toast.info("Signature saved as PNG");
                }
            };

            // Set the image source to the object URL
            img.src = url;
        }
    };

    return (
        <div className="space-y-4 text-start">
            <div className="space-y-2">
                <Label htmlFor={register("id_type").name}>ID Type</Label>
                <Select onValueChange={(value: typeof ENUMS.ID_TYPES[number]) => setValue("id_type", value)}>
                    <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select ID Type" />
                    </SelectTrigger>
                    <SelectContent>
                        {ENUMS.ID_TYPES.map((type) => (
                            <SelectItem key={type} value={type}>
                                {type}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {errors.id_type && <p className="text-sm text-destructive">{errors.id_type.message}</p>}
            </div>
            <div className="space-y-2">
                <Label htmlFor={register("id_number").name}>ID Number</Label>
                <Input id={register("id_number").name} {...register("id_number")} className="block w-full rounded-md border p-2" />
                {errors.id_number && <p className="text-sm text-destructive">{errors.id_number.message}</p>}
            </div>
            <div className="space-y-2">
                <Label htmlFor={register("id_photo").name}>ID Photo</Label>
                <Input id={register("id_photo").name} {...register("id_photo")} type="file" className="block w-full rounded-md border px-2" />
                {errors.id_photo && <p className="text-sm text-destructive">{errors.id_photo.message}</p>}
            </div>
            <div className="space-y-2">
                <Label htmlFor={register("bvn").name}>BVN</Label>
                <Input id={register("bvn").name} {...register("bvn")} className="block w-full rounded-md border p-2" />
                {errors.bvn && <p className="text-sm text-destructive">{errors.bvn.message}</p>}
            </div>
            <div className="space-y-2">
                <Label htmlFor={register("bvn_photo").name}>BVN Photo</Label>
                <Input id={register("bvn_photo").name} {...register("bvn_photo")} type="file" className="block w-full rounded-md border px-2" />
                {errors.bvn_photo && <p className="text-sm text-destructive">{errors.bvn_photo.message}</p>}
            </div>
            <div className="space-y-2">
                <Label htmlFor={register("signature").name}>Signature</Label>
                <Signature
                    id={register("signature").name}
                    {...register("signature")}
                    ref={signatureRef}
                    options={{
                        size: 6,
                        simulatePressure: true,
                        smoothing: 0.4,
                        thinning: 0.7,
                    }}
                    onChange={(e) => console.log(e)}
                    className="rounded-md border p-2" />
                <div className="flex justify-start gap-2">
                    <Button type="button" variant="outline" onClick={() => signatureRef.current?.clear()}>Clear</Button>
                    <Button type="button" onClick={() => handleSaveSignature()}>Save</Button>
                </div>
            </div>
        </div>
    );
}

// Residential information form component
const ResidentialForm = () => {
    const { register, formState: { errors }, setValue } = useFormContext<ResidentialInformation>();

    return (
        <div className="space-y-4 text-start">
            <div className="space-y-2">
                <Label htmlFor={register("state").name}>State</Label>
                <Select onValueChange={(value: typeof ENUMS.STATES[number]) => setValue("state", value)}>
                    <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select State" />
                    </SelectTrigger>
                    <SelectContent>
                        {ENUMS.STATES.map((state) => (
                            <SelectItem key={state} value={state}>
                                {state}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {errors.state && <p className="text-sm text-destructive">{errors.state.message}</p>}
            </div>
            <div className="space-y-2">
                <Label htmlFor={register("lga").name}>LGA</Label>
                <Select onValueChange={(value: typeof ENUMS.LGAS[number]) => setValue("lga", value)}>
                    <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select LGA" />
                    </SelectTrigger>
                    <SelectContent>
                        {ENUMS.LGAS.map((lga) => (
                            <SelectItem key={lga} value={lga}>
                                {lga}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {errors.lga && <p className="text-sm text-destructive">{errors.lga.message}</p>}
            </div>
        </div>
    );
}

// Cluster information form component
const ClusterForm = () => {
    const { register, formState: { errors }, setValue } = useFormContext<ClusterInformation>();

    useEffect(() => {
        setValue("country", "Nigeria");
    }, [setValue]);

    return (
        <div className="space-y-4 text-start">
            <div className="space-y-2">
                <Label htmlFor={register("cluster").name}>Cluster</Label>
                <Input id={register("cluster").name} {...register("cluster")} className="block w-full rounded-md border p-2" />
                {errors.cluster && <p className="text-sm text-destructive">{errors.cluster.message}</p>}
            </div>
            <div className="space-y-2">
                <Label htmlFor={register("cooperative_name").name}>Cooperative Name</Label>
                <Input id={register("cooperative_name").name} {...register("cooperative_name")} className="block w-full rounded-md border p-2" />
                {errors.cooperative_name && <p className="text-sm text-destructive">{errors.cooperative_name.message}</p>}
            </div>
            <div className="space-y-2">
                <Label htmlFor={register("cooperative_role").name}>Cooperative Role</Label>
                <Select onValueChange={(value: typeof OUTGROWER_COOPERATIVE_ROLES[number]) => setValue("cooperative_role", value)}>
                    <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select Role" />
                    </SelectTrigger>
                    <SelectContent>
                        {OUTGROWER_COOPERATIVE_ROLES.map((role) => (
                            <SelectItem key={role} value={role}>
                                {role}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {errors.cooperative_role && <p className="text-sm text-destructive">{errors.cooperative_role.message}</p>}
            </div>
            <div className="space-y-2">
                <Label htmlFor={register("current_credit_package").name}>Current Credit Package</Label>
                <Select onValueChange={(value: typeof ENUMS.INPUT_PACKAGES[number]) => setValue("current_credit_package", value)}>
                    <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select Package" />
                    </SelectTrigger>
                    <SelectContent>
                        {ENUMS.INPUT_PACKAGES.map((pkg) => (
                            <SelectItem key={pkg} value={pkg}>
                                {pkg}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground">IG - Ingrower, OG - Outgrower</p>
                {errors.current_credit_package && <p className="text-sm text-destructive">{errors.current_credit_package.message}</p>}
            </div>
            <div className="space-y-2">
                <Label htmlFor={register("country").name}>Country</Label>
                <Input id={register("country").name} {...register("country")} value="Nigeria" className="block w-full rounded-md border p-2" readOnly />
                {errors.country && <p className="text-sm text-destructive">{errors.country.message}</p>}
            </div>
            <div className="space-y-2">
                <Label htmlFor={register("commodity").name}>Commodity</Label>
                <Select onValueChange={(value: typeof ENUMS.COMMODITIES[number]) => setValue("commodity", value)}>
                    <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select Commodity" />
                    </SelectTrigger>
                    <SelectContent>
                        {ENUMS.COMMODITIES.map((commodity) => (
                            <SelectItem key={commodity} value={commodity}>
                                {commodity}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {errors.commodity && <p className="text-sm text-destructive">{errors.commodity.message}</p>}
            </div>
        </div>
    );
}

const { Stepper, useStepper } = defineStepper(
    {
        id: "personal_information",
        title: "Personal Information",
        schema: personalInformationSchema,
        component: PersonalInfoForm,
    },
    {
        id: "means_of_identification",
        title: "Identification",
        schema: meansOfIdentificationSchema,
        component: IdentificationForm,
    },
    {
        id: "residential_information",
        title: "Residence",
        schema: residentialInformationSchema,
        component: ResidentialForm,
    },
    {
        id: "cluster_information",
        title: "Cluster",
        schema: clusterInformationSchema,
        component: ClusterForm,
    }
)

export const OutgrowerFarmerOnboardingForm = () => {
    return (
        <Stepper.Provider variant="vertical" tracking>
            <FormComponent />
        </Stepper.Provider>
    );
}

const FormComponent = () => {
    const methods = useStepper();
    const isMobile = useMediaQuery("(max-width: 768px)");

    const form = useForm<
        z.infer<typeof personalInformationSchema> |
        z.infer<typeof meansOfIdentificationSchema> |
        z.infer<typeof residentialInformationSchema> |
        z.infer<typeof clusterInformationSchema>
    >({
        mode: "onBlur",
        resolver: zodResolver(methods.current.schema as any)
    });

    const onSubmit = async (data: z.infer<typeof methods.current.schema>) => {
        const farmer_id = form.getValues("phone_number");

        if (methods.isFirst) {
            const existingFarmer = await db.outgrower_farmers.get(farmer_id);
            if (existingFarmer) {
                toast.error("Outgrower farmer already exists");
                return;
            }

            db.outgrower_farmers.add({
                ...data,
                phone_number: farmer_id,
                status: "pending",
                creation: new Date().toISOString(),
                modified: new Date().toISOString(),
            })
        }

        await db.outgrower_farmers.update(farmer_id, {
            ...data,
            modified: new Date().toISOString(),
        })

        if (!methods.isLast) {
            methods.next();
            return;
        }

        await db.outgrower_farmers.update(farmer_id, {
            status: "unsynced",
            modified: new Date().toISOString(),
        });

        form.reset();
        methods.reset();
        toast.success("Outgrower farmer onboarded successfully!");
    }

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <Stepper.Navigation>
                    {methods.all.map((step) => (
                        <Stepper.Step
                            key={step.id}
                            of={step.id}
                            type="button"
                            onClick={async () => {
                                const valid = await form.trigger();
                                if (!valid) return;
                                methods.goTo(step.id);
                            }}
                        >
                            <Stepper.Title>{step.title}</Stepper.Title>
                            {methods.when(step.id, () =>
                                <Stepper.Panel>
                                    {methods.switch({
                                        personal_information: () => <PersonalInfoForm />,
                                        means_of_identification: () => <IdentificationForm />,
                                        residential_information: () => <ResidentialForm />,
                                        cluster_information: () => <ClusterForm />
                                    })}
                                    <Stepper.Controls className="mt-4">
                                        <Button variant="secondary" onClick={methods.prev} disabled={methods.isFirst}>Previous</Button>
                                        <Button
                                            type="submit"
                                            onClick={() => {
                                                form.trigger();
                                            }}>
                                            {methods.isLast ? "Submit" : "Next"}
                                        </Button>
                                    </Stepper.Controls>
                                </Stepper.Panel>
                            )}
                        </Stepper.Step>
                    ))}
                </Stepper.Navigation>
            </form>
        </Form>
    );
}
